<template>
    <div class="device-alarm-pipelines-view">
        <div class="info-block info-block--default">
            <div v-if="!alarmProcessed" class="header-default-mode">
                <h1>Alarms for device with hardware id: {{ hardwareId }} </h1>
            </div>
            <div v-if="alarmProcessed" class="header-edit-mode">
                <h1 class="header-edit-mode__title">Edit Alarm:</h1>
                <device-alarms-menu
                    :pipelines="pipelines"
                    :alarm-processed="alarmProcessed"
                    @changeProcessedAlarm="onChangeProcessedAlarm"
                ></device-alarms-menu>
            </div>
        </div>
        <v-container class="content-container" fluid>
            <v-layout row justify-center>
                <v-flex class="section">
                    <div v-if="!alarmProcessed" class="alarm-cards__wrapper">
                        <device-alarm-card
                            v-for="alarm of activeAlarms"
                            :key="alarm.alarmKey"
                            :type="'edit'"
                            :alarm="alarm"
                            @isAlarmProcessed="onAlarmProcessed"
                            @resetAlarm="onResetAlarm"
                        ></device-alarm-card>
                        <device-alarm-card
                            v-if="inactiveAlarms.length"
                            :type="'add'"
                            :alarm="inactiveAlarms[0]"
                            @isAlarmProcessed="onAlarmProcessed"
                        ></device-alarm-card>
                    </div>
                    <div v-if="alarmProcessed" class="alarm-cards__form-wrapper">
                        <device-alarms-workflow
                            :alarm-processed="alarmProcessed"
                            @goBack="onNavigateBack"
                            @updateWorkflow="onUpdateWorkflow"
                        ></device-alarms-workflow>
                        <div class="alarm-cards__navigation">
                            <sp-btn text="Navigate back" @click.native="clearAlarmProcessed" icon="go-back"></sp-btn>
                        </div>
                    </div>
                </v-flex>
            </v-layout>
            <sp-loader v-if="isLoading"></sp-loader>
        </v-container>
    </div>
</template>

<script>

import DeviceAlarmCard from '@/views/_components/alarmsPipelines/DeviceAlarmCard'
import DeviceAlarmsWorkflow from '@/views/_components/alarmsPipelines/DeviceAlarmsWorkflow'
import DeviceAlarmsMenu from '@/views/_components/alarmsPipelines/DeviceAlarmsMenu'
import SpLoader from '@/components/atoms/Loader'
import toast from '@/plugins/izitoast'
import { arrayToBitFlag, deleteProperties, bitFlagToArray, JSONClone } from '@/helpers/Utils'
import SpBtn from '@/components/forms/SpBtn'

import { mapGetters } from 'vuex'

import DevicesSettingsApi from '@/api/DevicesSettingsApi'
import DeviceApi from '@/api/DeviceApi'

const devicesSettingsApi = new DevicesSettingsApi()
const deviceApi = new DeviceApi()

export default {
    name: 'DeviceAlarmPipelines',
    components: {
        DeviceAlarmCard,
        DeviceAlarmsWorkflow,
        DeviceAlarmsMenu,
        SpBtn,
        SpLoader
    },
    mounted () {
        this.getHardwareId()
    },
    data () {
        return {
            isLoading: false,
            pipelines: null,
            deviceModel: null,
            deviceId: this.$route.params.deviceUuid,
            hardwareId: null,
            activeAlarms: [],
            inactiveAlarms: [],
            alarmProcessed: null,
            availableChannels: []
        }
    },
    props: {
    },
    provide () {
        return {
            availableChannels: this.availableChannels
        }
    },
    computed: {
        ...mapGetters({
            getDeviceModels: 'dictionaryModule/getDeviceModels',
            getChannels: 'dictionaryModule/getChannels'
        })
    },
    methods: {
        async getDevicesAlarmsPipelinesSettings () {
            if (!this.hasAllRoles(['DeviceView'])) {
                return
            }

            this.isLoading = true
            const response = await devicesSettingsApi.getDevicesAlarmPipelinesSettings(this.hardwareId)
            if (!response.isSuccess) {
                this.isLoading = false
                return
            }

            this.pipelines = response.payload.pipelines.map(alarm => {
                if (!alarm.pipelines) {
                    this.convertToCompoundDataModel(alarm)
                } else {
                    alarm.pipelines.forEach(workflowAlarm => {
                        workflowAlarm.id = '_' + Math.random().toString(36).substr(2, 9)
                        if (workflowAlarm.channels) workflowAlarm.channels = bitFlagToArray(workflowAlarm.channels)
                    })
                }
                return alarm
            })

            this.pipelines.forEach(alarm => {
                if (0 !== alarm.type) {
                    this.activeAlarms.push(alarm)
                } else {
                    this.inactiveAlarms.push(alarm)
                }
            })
            this.isLoading = false
        },
        resetData () {
            this.activeAlarms = []
            this.inactiveAlarms = []
            this.pipelines = []
        },
        onAlarmProcessed (alarm) {
            this.alarmProcessed = JSONClone(alarm)
        },
        onNavigateBack (value) {
            this.alarmProcessed = value
        },
        onChangeProcessedAlarm (alarmKey) {
            this.alarmProcessed = JSONClone(this.pipelines.filter(alarm => alarm.alarmKey === alarmKey)[0])
        },
        async onResetAlarm (alarm) {
            if (!this.hasAllRoles(['DeviceUpdate'])) {
                return
            }

            const response = await devicesSettingsApi.resetDevicesAlarmPipelinesSettings({
                alarmKey: alarm.alarmKey,
                hardwareId: this.hardwareId
            })
            if (!response.isSuccess) {
                return
            }

            this.resetData()
            this.getDevicesAlarmsPipelinesSettings()
            toast.success('Alarm has been successfully reseted')
        },

        clearAlarmProcessed () {
            this.alarmProcessed = null
        },

        async onUpdateWorkflow (alarm) {
            if (!this.hasAllRoles(['DeviceUpdate'])) {
                return
            }

            const clonedAlarm = JSONClone(alarm)
            deleteProperties(clonedAlarm, ['channels', 'isOverriden'])

            clonedAlarm.pipelines.forEach(workflowAlarm => {
                if (workflowAlarm.channels) workflowAlarm.channels = arrayToBitFlag(workflowAlarm.channels)
                deleteProperties(workflowAlarm, ['id'])
            })

            clonedAlarm.type = 2

            const response = await devicesSettingsApi.createDevicesAlarmPipelinesSettings({
                hardwareId: this.hardwareId,
                pipeline: clonedAlarm
            })

            if (!response.isSuccess) {
                return
            }

            this.resetData()
            this.getDevicesAlarmsPipelinesSettings()
            toast.success('Pipeline updated')
        },
        defineAvailableChannels () {
            const deviceModel = this.getDeviceModels.filter(device => device.id === this.deviceModel.id)[0]
            const deviceModelChannels = bitFlagToArray(deviceModel ? deviceModel.availableChannels : 0)
            const channels = this.getChannels.filter(channel => deviceModelChannels.includes(channel.id))
            this.availableChannels = this.availableChannels.splice(0, this.availableChannels.length, ...channels)
        },
        convertToCompoundDataModel (alarm) {
            const outerPropertiesToDelete = ['channels', 'eventClassification', 'eventType']
            const innerPropertiesToDelete = ['alarmKey', 'isOverriden']

            switch (alarm.type) {
                case 1:
                case 3:
                    if (alarm.channels) {
                        alarm.channels = bitFlagToArray(alarm.channels)
                    }
                    alarm.pipelines = [{ ...alarm }]
                    deleteProperties(alarm, outerPropertiesToDelete)
                    deleteProperties(alarm.pipelines[0], innerPropertiesToDelete)
                    alarm.type = 2
                    alarm.pipelines[0].id = '_' + Math.random().toString(36).substr(2, 9)
                    break
                case 0:
                    alarm.pipelines = []
            }
        },
        async getHardwareId () {
            if (!this.hasAllRoles(['DeviceView'])) {
                return
            }

            this.isLoading = true
            const response = await deviceApi.getDevice(this.deviceId)
            if (!response.isSuccess) {
                this.isLoading = false
                return
            }

            this.deviceModel = response.payload.deviceModel
            this.defineAvailableChannels()

            this.hardwareId = response.payload.hardwareId
            this.getDevicesAlarmsPipelinesSettings()
        }
    }
}
</script>

<style lang="scss" scoped>
.device-alarm-pipelines-view {
    .info-block {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        .action-block {
            display: flex;
            align-items: center;
        }
        h1 {
            font-size: 16px;
            font-weight: 400;
        }
    }
    .section {
        padding: 0 10px;
        &:last-child {
            border-right-color: transparent;
        }
    }
}
::v-deep .device-select {
    background: #fff !important;
    margin: 0 30px 0 0;
    max-width: 200px;
    .v-input__slot {
        margin: 0 0 0 0 !important;
        min-height: 40px !important;
    }
    .v-text-field__details {
        display: none !important;
    }
    .v-input__append-inner {
        margin: 7px 0 0 0 !important;
    }
    .v-select__slot {
        label {
            top: 10px !important
        }
    }
}

.alarm-cards{
    &__form-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    &__navigation {
        align-self: flex-start;
        margin-top: 30px;
    }
}
.alarm-cards__wrapper {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(430px, 1fr));
    grid-template-rows: minmax(244px, auto-fit);
    column-gap: 1.5rem;
    row-gap: 1.5rem;
}
.alarm-cards__pick-device {
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    &__info {
        text-align: center;
        font-size: 18px;
    }
}
.header-default-mode {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}
.header-edit-mode {
    display: flex;
    &__title {
        margin-right: 15px;
    }
}
.content-container {
    min-height: 400px;
}
.close {
    position: static;
    cursor: pointer;

    &:hover {
        opacity: 0.7;
    }
}
</style>
